import type { User } from "firebase/auth";
import { readImageFileAsDataUrl } from "@/character/frame";
import {
  formatAoeRangeBonusPercent,
  isAoeAttackSkill,
  skillAoeRangeCardsApplied,
  skillAoeRangeMultiplier,
} from "@/character/skills/aoe-range";
import {
  allocateBuffStatPointForCards,
  deallocateBuffStatPointForCards,
  buffAllocationFromSkill,
  buffStatBonusRecord,
  skillApCardsApplied,
  skillBuffApBudget,
  skillBuffApBudgetForCards,
  skillBuffApRemainingForCards,
  totalBuffApSpent,
} from "@/character/skills/buff-ap";
import {
  formatBuffDurationSeconds,
  skillBuffDurationCardsApplied,
  skillEffectiveBuffDurationMs,
} from "@/character/skills/buff-duration";
import {
  SKILL_AP_PER_CARD,
  SKILL_BUFF_BASE_AP,
  SKILL_BUFF_DURATION_SEC_PER_CARD,
  SKILL_COOLDOWN_SEC_PER_CARD,
  SKILL_DEFAULT_BUFF_DURATION_MS,
  SKILL_LIBRARY_MAX,
  SKILL_SLOT_MAX,
  formatSkillCooldownSeconds,
  SKILL_TEMPLATE_FRAME_HEIGHT,
  SKILL_TEMPLATE_FRAME_WIDTH,
} from "@/character/skills/constants";
import {
  migrateLegacyGlobalCooldownCards,
  skillCooldownCardsApplied,
  skillEffectiveCooldownMs,
} from "@/character/skills/cooldown-upgrade";
import { mountSkillSheetPreview } from "@/character/skills/mount-skill-sheet-preview";
import {
  mountSkillUsageDemo,
  type SkillUsageDemoState,
} from "@/character/skills/mount-skill-usage-demo";
import { parseSkillSpritesheet } from "@/character/skills/parse-skill-sheet";
import {
  alignSkillLibrarySlotsWithInventory,
} from "@/character/skills/skill-library-limit";
import { getSkillPoolConfig } from "@/pet/skills/skill-pool-config";
import {
  frameLayoutFromOutputSheet,
  normalizeSkillFrameLayout,
  skillGridTuningForImage,
  type SkillFrameLayout,
} from "@/character/skills/skill-frame-layout";
import {
  assignSkillToSlot,
  loadSkillBar,
  newSkillId,
  saveSkillBar,
  skillById,
} from "@/character/skills/storage";
import type {
  SkillAttackPattern,
  SkillBarState,
  SkillDefinition,
  SkillKind,
} from "@/character/skills/types";
import type { CharacterGridSpec } from "@/character/grid";
import { mountCharacterIdlePreview } from "@/character/character-preview";
import {
  emptyAllocation,
  STAT_IDS,
  STAT_LABEL_TH,
  STAT_PLUS_HINT_TH,
  type StatAllocation,
  type StatId,
} from "@/game/model/character-stats";
import { downloadSkillMotionTemplate } from "@/character/template";
import { loadCharacterProfile } from "@/character/storage";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import {
  applyAutoBackgroundRemovalFromDataUrl,
  applyBackgroundRemovalFromDataUrl,
  bindAutoRemoveBlackBgCheckbox,
  bindAutoRemoveWhiteBgCheckbox,
  isAutoRemoveBlackBgEnabled,
  isAutoRemoveWhiteBgEnabled,
} from "@/home/remove-white-bg";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export type SkillsPanelOptions = {
  getSkillBar: () => SkillBarState;
  getEconomy: () => HomeEconomy;
  /** In-game modal: hide page nav and use compact chrome. */
  embedded?: boolean;
  /** Player hotbar skills vs pet skill library cards. */
  cardPool?: import("@/pet/skills/skill-pool-config").SkillCardPool;
  /** Pet skill preview: actor under skill FX (defaults to character profile). */
  getUsageDemoActor?: () => { spritesheetDataUrl: string; grid: CharacterGridSpec } | null;
  /** Idle preview when no skill selected (embedded panel). */
  mountIdlePreview?: (host: HTMLElement) => () => void;
  /** Embedded in character sheet: editor modal lives in panel root, not #home-skill-editor-modal. */
  editorInRoot?: boolean;
  /** Pet detail modal: hotbar + library only (preview/stats live in parent). */
  panelLayout?: "default" | "pet-detail";
  onSkillBarUpdated?: (bar: SkillBarState) => void;
  onEconomyUpdated?: (economy: HomeEconomy) => void;
};

export async function mountSkillsPage(root: HTMLElement, user: User): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  root.className = "page-shell page-skills";
  let skillBar = await loadSkillBar(user.uid);
  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();
  const legacyCd = migrateLegacyGlobalCooldownCards(skillBar, economy);
  skillBar = legacyCd.bar;
  economy = legacyCd.economy;
  if (legacyCd.changed) {
    void saveSkillBar(user.uid, skillBar);
    void saveHomeEconomyToIdb(user.uid, economy);
  }
  const alignedEconomy = alignSkillLibrarySlotsWithInventory(skillBar.library.length, economy);
  if (alignedEconomy !== economy) {
    economy = alignedEconomy;
    void saveHomeEconomyToIdb(user.uid, economy);
  }

  const panel = mountSkillsPanel(root, user, profile, {
    getSkillBar: () => skillBar,
    getEconomy: () => economy,
    embedded: false,
    onSkillBarUpdated: (bar) => {
      skillBar = bar;
    },
    onEconomyUpdated: (next) => {
      economy = next;
    },
  });
  return () => panel.teardown();
}

export type SkillsPanelHandle = {
  teardown: () => void;
  refresh: () => void;
};

export function mountSkillsPanel(
  root: HTMLElement,
  user: User,
  profile: NonNullable<ReturnType<typeof loadCharacterProfile>>,
  options: SkillsPanelOptions,
): SkillsPanelHandle {
  const petDetailLayout = options.panelLayout === "pet-detail";
  if (petDetailLayout) {
    root.className = "pet-detail-skills-root home-skills-panel-root";
  } else if (options.embedded) {
    root.className = "home-skills-panel-root";
  } else {
    root.className = "skills-game-frame-outer page-skills-inner";
  }

  const bar = () => options.getSkillBar();
  const eco = () => options.getEconomy();
  const poolCfg = getSkillPoolConfig(options.cardPool ?? "player");
  let pendingAssignSlot: number | null = null;
  let editorOpen = false;
  let draftEditingId: string | null = null;
  let draftName = "";
  let draftSheet: string | null = null;
  let draftIcon: string | null = null;
  let draftGrid = profile.grid;
  let draftKind: SkillKind = "attack";
  let draftAttackPattern: SkillAttackPattern = "single";
  let draftDurationSec = 5;
  let draftBuffAllocation: StatAllocation = emptyAllocation();
  let draftRawUrl: string | null = null;
  let draftFrameLayout: SkillFrameLayout = { frames: [] };
  let stopPreview: (() => void) | undefined;
  let stopUsageDemo: (() => void) | undefined;
  let stopCharPreview: (() => void) | undefined;
  let previewSkillId: string | null = null;

  if ((options.cardPool ?? "player") === "player") {
    const alignedEco = alignSkillLibrarySlotsWithInventory(bar().library.length, eco());
    if (alignedEco.skillLibrarySlotsUnlocked !== eco().skillLibrarySlotsUnlocked) {
      options.onEconomyUpdated?.(alignedEco);
    }
  }

  const unlockedSlots = (): number => poolCfg.slotsUnlocked(eco());
  const showUsageDemo = () =>
    !options.embedded || (options.embedded && previewSkillId != null);

  const useHomeSkillEditor = options.embedded && !options.editorInRoot;

  const editorModal = (): HTMLElement | null =>
    useHomeSkillEditor
      ? document.getElementById("home-skill-editor-modal")
      : root.querySelector<HTMLElement>("#skill-editor-modal-local");

  const editorHost = (): HTMLElement | null =>
    useHomeSkillEditor
      ? document.getElementById("home-skill-editor-host")
      : root.querySelector<HTMLElement>("#skill-editor-host-local");

  const resetDraft = () => {
    draftEditingId = null;
    draftName = "";
    draftSheet = null;
    draftIcon = null;
    draftGrid = profile.grid;
    draftKind = "attack";
    draftAttackPattern = "single";
    draftDurationSec = 5;
    draftBuffAllocation = emptyAllocation();
    draftRawUrl = null;
    draftFrameLayout = { frames: [] };
  };

  const draftEditorSkillApCards = (): number | undefined => {
    if (!draftEditingId) return 0;
    return skillById(bar(), draftEditingId)?.skillApCards;
  };

  const draftEditorBuffDurationCards = (): number => {
    if (!draftEditingId) return 0;
    return skillById(bar(), draftEditingId)?.buffDurationCards ?? 0;
  };

  const demoFromSkill = (skill: SkillDefinition): SkillUsageDemoState => ({
    name: skill.name,
    kind: skill.kind,
    iconDataUrl: skill.iconDataUrl,
    spritesheetDataUrl: skill.spritesheetDataUrl,
    grid: skill.grid,
    durationSec:
      skill.kind === "buff"
        ? skillEffectiveBuffDurationMs(skill) / 1000
        : draftDurationSec,
  });

  const usageDemoState = (): SkillUsageDemoState => {
    if (editorOpen) {
      const durationSec =
        draftKind === "buff"
          ? (SKILL_DEFAULT_BUFF_DURATION_MS +
              draftEditorBuffDurationCards() * (SKILL_BUFF_DURATION_SEC_PER_CARD * 1000)) /
            1000
          : draftDurationSec;
      return {
        name: draftName,
        kind: draftKind,
        iconDataUrl: draftIcon,
        spritesheetDataUrl: draftSheet,
        grid: draftGrid,
        durationSec,
      };
    }
    if (previewSkillId) {
      const picked = skillById(bar(), previewSkillId);
      if (picked) return demoFromSkill(picked);
    }
    const slotSkillId = bar().slots.find((id) => id != null) ?? null;
    const preview =
      (slotSkillId ? skillById(bar(), slotSkillId) : undefined) ?? bar().library[0];
    if (preview) return demoFromSkill(preview);
    return {
      name: "",
      kind: "attack",
      iconDataUrl: null,
      spritesheetDataUrl: null,
      grid: draftGrid,
      durationSec: 5,
    };
  };

  const processSkillImage = async (dataUrl: string, resetFrameLayout: boolean) => {
    let url = dataUrl;
    if (isAutoRemoveWhiteBgEnabled() || isAutoRemoveBlackBgEnabled()) {
      url = await applyAutoBackgroundRemovalFromDataUrl(url);
    }
    const probe = new Image();
    await new Promise<void>((resolve, reject) => {
      probe.onload = () => resolve();
      probe.onerror = () => reject(new Error("โหลดภาพไม่สำเร็จ"));
      probe.src = url;
    });
    draftRawUrl = url;
    if (resetFrameLayout) {
      draftFrameLayout = skillGridTuningForImage(probe.naturalWidth, probe.naturalHeight);
    } else {
      draftFrameLayout = normalizeSkillFrameLayout(
        draftFrameLayout,
        probe.naturalWidth,
        probe.naturalHeight,
      );
    }
    await applySkillParse();
  };

  const applySkillParse = async () => {
    if (!draftRawUrl) {
      draftSheet = null;
      draftIcon = null;
      return;
    }
    const parsed = await parseSkillSpritesheet(draftRawUrl, draftFrameLayout);
    draftSheet = parsed.spritesheetDataUrl;
    draftIcon = parsed.iconDataUrl;
    draftGrid = parsed.grid;
    draftFrameLayout = parsed.frameLayout;
  };

  const loadSkillIntoDraft = async (skill: SkillDefinition) => {
    draftEditingId = skill.id;
    draftName = skill.name;
    draftKind = skill.kind;
    draftAttackPattern = skill.attackPattern ?? "single";
    draftDurationSec = 5;
    draftBuffAllocation =
      skill.kind === "buff" ? buffAllocationFromSkill(skill) : emptyAllocation();
    draftRawUrl = skill.sourceSheetDataUrl ?? skill.spritesheetDataUrl;
    const probe = new Image();
    await new Promise<void>((resolve, reject) => {
      probe.onload = () => resolve();
      probe.onerror = () => reject(new Error("โหลดภาพสกิลไม่สำเร็จ"));
      probe.src = draftRawUrl!;
    });
    const layoutSeed =
      skill.sourceSheetDataUrl && skill.frameLayout
        ? skill.frameLayout
        : frameLayoutFromOutputSheet(skill.grid);
    draftFrameLayout = normalizeSkillFrameLayout(
      layoutSeed,
      probe.naturalWidth,
      probe.naturalHeight,
    );
    await applySkillParse();
  };

  const openCreateEditor = () => {
    const gate = poolCfg.canAddToLibrary(bar().library.length, eco());
    if (!gate.ok) {
      showError(gate.reason ?? "ไม่สามารถสร้างสกิลได้");
      return;
    }
    resetDraft();
    editorOpen = true;
    render();
  };

  const openEditEditor = (skillId: string) => {
    const skill = skillById(bar(), skillId);
    if (!skill) return;
    void loadSkillIntoDraft(skill)
      .then(() => {
        editorOpen = true;
        render();
      })
      .catch((err) => {
        showError(err instanceof Error ? err.message : "โหลดสกิลไม่สำเร็จ");
      });
  };

  const closeEditor = () => {
    editorOpen = false;
    resetDraft();
    render();
  };

  const mountUsageDemo = () => {
    stopUsageDemo?.();
    stopUsageDemo = undefined;
    if (!showUsageDemo()) return;
    const host = root.querySelector<HTMLElement>("#skill-usage-demo-host");
    if (!host) return;
    const previewSkill = previewSkillId ? skillById(bar(), previewSkillId) : undefined;
    const demoSkill =
      previewSkill ?? (options.embedded ? undefined : bar().library[0]);
    const actor = options.getUsageDemoActor?.() ?? {
      spritesheetDataUrl: profile.spritesheetDataUrl,
      grid: profile.grid,
    };
    stopUsageDemo = mountSkillUsageDemo(host, actor, usageDemoState(), {
      compact: options.embedded,
      autoPlay: Boolean(demoSkill),
      cooldownMs: demoSkill ? skillEffectiveCooldownMs(demoSkill) : undefined,
    });
  };

  const mountPreviewIfNeeded = () => {
    stopPreview?.();
    stopPreview = undefined;
    const host = editorHost()?.querySelector<HTMLElement>("#skill-preview-host");
    if (!host || !draftRawUrl || !editorOpen) return;
    stopPreview = mountSkillSheetPreview(host, draftRawUrl, draftFrameLayout, (next) => {
      draftFrameLayout = next;
      void applySkillParse()
        .then(() => {
          updateSubmitDisabled();
          mountUsageDemo();
        })
        .catch((err) => {
          showError(err instanceof Error ? err.message : "ปรับกริดไม่สำเร็จ");
        });
    });
  };

  const persist = () => {
    options.onSkillBarUpdated?.(bar());
    if ((options.cardPool ?? "player") === "player") {
      void saveSkillBar(user.uid, bar()).catch(() => {
        /* best-effort */
      });
    }
  };

  const persistEconomy = () => {
    options.onEconomyUpdated?.(eco());
    void saveHomeEconomyToIdb(user.uid, eco()).catch(() => {
      /* best-effort */
    });
  };

  const buffApEditorHtml = () => {
    if (draftKind !== "buff") return "";
    const apCards = draftEditorSkillApCards();
    const budget = skillBuffApBudgetForCards(apCards);
    const spent = totalBuffApSpent(draftBuffAllocation);
    const remaining = skillBuffApRemainingForCards(apCards, draftBuffAllocation);
    const durationCards = draftEditorBuffDurationCards();
    const durationMs =
      SKILL_DEFAULT_BUFF_DURATION_MS + durationCards * (SKILL_BUFF_DURATION_SEC_PER_CARD * 1000);
    const rows = STAT_IDS
      .map((id) => {
        const pts = draftBuffAllocation[id];
        const hint = STAT_PLUS_HINT_TH[id];
        return `
          <div class="stat-row skill-buff-stat-row" data-buff-stat-id="${id}">
            <span class="stat-row-label" title="${hint}">${STAT_LABEL_TH[id]}</span>
            <span class="stat-row-pts">${pts}</span>
            <div class="stat-row-actions">
              <button type="button" class="btn btn-ghost btn-xs stat-row-plus stat-row-plus--one" data-buff-stat-minus="${id}" title="ลด 1 แต้ม (ก่อนบันทึก)" ${
                pts > 0 ? "" : "disabled"
              }>−1</button>
              <button type="button" class="btn btn-ghost btn-xs stat-row-plus stat-row-plus--one" data-buff-stat-plus="${id}" title="${hint}" ${
                remaining > 0 ? "" : "disabled"
              }>+1</button>
            </div>
          </div>`;
      })
      .join("");
    return `
        <div class="skill-buff-ap-panel" id="skill-buff-ap-panel">
          <p class="hint skill-buff-duration-hint">ระยะบัฟ <strong>${formatBuffDurationSeconds(durationMs)}</strong> วิ (เริ่ม 5 วิ · บัตรเวลา ${durationCards} ใบ · +${SKILL_BUFF_DURATION_SEC_PER_CARD} วิ/ใบ)</p>
          <p class="field-label">ลงสเตตบัฟ (AP)</p>
          <p class="hint skill-buff-ap-summary">พื้นฐาน ${SKILL_BUFF_BASE_AP} AP · ใช้แล้ว ${spent}/${budget} · คงเหลือ <strong>${remaining}</strong></p>
          <p class="hint skill-buff-ap-cards">บัตร AP ติดสกิล ${Math.max(0, Math.floor(apCards ?? 0))} ใบ (+${SKILL_AP_PER_CARD} AP/ใบ)</p>
          <div class="stat-rows skill-buff-stat-rows">${rows}</div>
        </div>`;
  };

  const editorPanelHtml = () => {
    const isEdit = draftEditingId != null;
    const title = isEdit ? "แก้ไขสกิล" : "สร้างสกิลใหม่";
    const submitLabel = isEdit ? "บันทึกการแก้ไข" : "เพิ่มเข้าคลัง";
    return `
      <div class="skill-editor-panel" id="skill-editor-panel">
        <div class="skill-editor-head">
          <h3 class="skill-editor-title" id="skill-editor-title">${title}</h3>
          <button type="button" class="skill-editor-close" id="skill-editor-close" data-close-skill-editor aria-label="ปิด">×</button>
        </div>
        <div class="home-actions skill-editor-tools">
          <button type="button" id="skill-download-template" class="btn btn-ghost btn-sm">ดาวน์โหลดเทมเพลต</button>
        </div>
        <label class="field">
          <span class="field-label">ชื่อสกิล</span>
          <input type="text" id="skill-name" class="text-input" maxlength="24" placeholder="เช่น ฟ้าผ่า" value="${escapeHtml(draftName)}" />
        </label>
        <label class="field">
          <span class="field-label">ประเภท</span>
          <select id="skill-kind" class="text-input">
            <option value="attack" ${draftKind === "attack" ? "selected" : ""}>โจมตี — แสดงตอนใช้ / ตี</option>
            <option value="buff" ${draftKind === "buff" ? "selected" : ""}>บัฟ — แสดงจนหมดเวลา</option>
          </select>
        </label>
        <label class="field" id="skill-attack-pattern-wrap" ${draftKind === "attack" ? "" : "hidden"}>
          <span class="field-label">รูปแบบโจมตี</span>
          <select id="skill-attack-pattern" class="text-input">
            <option value="single" ${draftAttackPattern === "single" ? "selected" : ""}>เป้าเดียว — ตีเป้าที่กำลังสู้</option>
            <option value="aoe" ${draftAttackPattern === "aoe" ? "selected" : ""}>หมู่ — รอบตัว 1 ช่อง (3×3)</option>
          </select>
        </label>
        <div id="skill-buff-ap-wrap" ${draftKind === "buff" ? "" : "hidden"}>${buffApEditorHtml()}</div>
        <div class="skill-upload-options">
          <label class="home-remove-white-bg skill-remove-white-bg" title="ตัดพื้นขาว/อ่อนเมื่ออัปโหลด">
            <input type="checkbox" id="skill-remove-white-bg" />
            <span>ลบพื้นหลังขาว (อัตโนมัติเมื่ออัปโหลด)</span>
          </label>
          <label class="home-remove-white-bg skill-remove-black-bg" title="ตัดพื้นดำ/เข้มเมื่ออัปโหลด">
            <input type="checkbox" id="skill-remove-black-bg" />
            <span>ลบพื้นหลังดำ (อัตโนมัติเมื่ออัปโหลด)</span>
          </label>
          <button type="button" class="btn btn-ghost btn-sm" id="skill-apply-remove-bg" ${draftRawUrl ? "" : "disabled"}>
            ลบพื้นหลังจากภาพนี้
          </button>
        </div>
        <label class="btn btn-ghost sheet-upload">
          ${isEdit ? "เปลี่ยนภาพแอนิเมชัน" : "อัปโหลดแอนิเมชันสกิล"}
          <input type="file" id="skill-sheet-file" accept="image/png,image/jpeg,image/webp" hidden />
        </label>
        <div id="skill-preview-host" class="skill-preview-host"></div>
        <button type="button" id="skill-create-submit" class="btn btn-primary btn-block" ${draftSheet && draftName.trim() ? "" : "disabled"}>${submitLabel}</button>
        <p id="skill-form-error" class="form-error" role="alert" hidden></p>
      </div>`;
  };

  const mountCharPreview = () => {
    stopCharPreview?.();
    stopCharPreview = undefined;
    if (!options.embedded) return;
    const host = root.querySelector<HTMLElement>("#skills-char-preview-host");
    if (!host) return;
    if (options.mountIdlePreview) {
      stopCharPreview = options.mountIdlePreview(host);
      return;
    }
    stopCharPreview = mountCharacterIdlePreview(host, profile);
  };

  const render = () => {
    stopPreview?.();
    stopPreview = undefined;
    stopUsageDemo?.();
    stopUsageDemo = undefined;
    stopCharPreview?.();
    stopCharPreview = undefined;

    const libraryItems = bar().library
      .map((skill) => {
        const cards = skillCooldownCardsApplied(skill);
        const cdLabel = formatSkillCooldownSeconds(skillEffectiveCooldownMs(skill));
        const canUpgrade = poolCfg.cooldownCards(eco()) > 0;
        const apCards = skillApCardsApplied(skill);
        const canUpgradeAp = skill.kind === "buff" && poolCfg.apCards(eco()) > 0;
        const durationCards = skillBuffDurationCardsApplied(skill);
        const canUpgradeDuration =
          skill.kind === "buff" && poolCfg.buffDurationCards(eco()) > 0;
        const aoeRangeCards = skillAoeRangeCardsApplied(skill);
        const canUpgradeAoeRange =
          isAoeAttackSkill(skill) && poolCfg.aoeRangeCards(eco()) > 0;
        const aoeMeta =
          isAoeAttackSkill(skill)
            ? ` · กว้าง +${formatAoeRangeBonusPercent(skillAoeRangeMultiplier(skill))}% (บัตร ${aoeRangeCards})`
            : "";
        const buffApMeta =
          skill.kind === "buff"
            ? ` · ${formatBuffDurationSeconds(skillEffectiveBuffDurationMs(skill))}วิ · บัตรเวลา ${durationCards} · AP ${totalBuffApSpent(buffAllocationFromSkill(skill))}/${skillBuffApBudget(skill)} (บัตร AP ${apCards})`
            : "";
        const kindClass = skill.kind === "buff" ? "skill-inv-kind--buff" : "skill-inv-kind--attack";
        const selected = previewSkillId === skill.id ? " skill-inv-card--selected" : "";
        const cooldownBtn = `<button type="button" class="skill-inv-btn skill-inv-btn--accent" data-upgrade-skill="${escapeHtml(skill.id)}" ${canUpgrade ? "" : "disabled"} title="ใช้บัตรลดคูลดาวน์ −${SKILL_COOLDOWN_SEC_PER_CARD}วิ">ลดคูลดาวน์</button>`;
        const buffExtraBtns =
          skill.kind === "buff"
            ? `<button type="button" class="skill-inv-btn skill-inv-btn--accent" data-upgrade-duration-skill="${escapeHtml(skill.id)}" ${canUpgradeDuration ? "" : "disabled"} title="บัตรเวลา +${SKILL_BUFF_DURATION_SEC_PER_CARD}วิ">เพิ่มระยะเวลาสกิล</button>
            <button type="button" class="skill-inv-btn skill-inv-btn--accent" data-upgrade-ap-skill="${escapeHtml(skill.id)}" ${canUpgradeAp ? "" : "disabled"} title="ใช้บัตร AP +${SKILL_AP_PER_CARD}">+AP</button>`
            : "";
        const aoeExtraBtns =
          isAoeAttackSkill(skill)
            ? `<button type="button" class="skill-inv-btn skill-inv-btn--accent" data-upgrade-aoe-range-skill="${escapeHtml(skill.id)}" ${canUpgradeAoeRange ? "" : "disabled"} title="บัตรระยะ +1% กว้าง">เพิ่มระยะสกิล</button>`
            : "";
        return `
        <li class="skill-inv-card${selected}" data-skill-id="${escapeHtml(skill.id)}">
          <div class="skill-inv-icon" aria-hidden="true">
            <img src="${escapeHtml(skill.iconDataUrl)}" alt="" width="48" height="48" />
          </div>
          <div class="skill-inv-info">
            <p class="skill-inv-name">${escapeHtml(skill.name)}</p>
            <span class="skill-inv-kind ${kindClass}">${skill.kind === "buff" ? "บัฟ" : skill.attackPattern === "aoe" ? "โจมตีหมู่" : "โจมตี"}</span>
            <p class="skill-inv-meta">คูลดาวน์ ${cdLabel}วิ · บัตรคูลดาวน์ ${cards} ใบ${aoeMeta}${buffApMeta}</p>
          </div>
          <div class="skill-inv-actions${skill.kind === "buff" || isAoeAttackSkill(skill) ? " skill-inv-actions--buff" : ""}">
            ${cooldownBtn}
            ${aoeExtraBtns}
            ${buffExtraBtns}
            <button type="button" class="skill-inv-btn" data-edit-skill="${escapeHtml(skill.id)}">แก้ไข</button>
            <button type="button" class="skill-inv-btn skill-inv-btn--equip" data-assign-skill="${escapeHtml(skill.id)}">ใส่ช่อง</button>
            <button type="button" class="skill-inv-btn skill-inv-btn--danger" data-remove-skill="${escapeHtml(skill.id)}">ลบ</button>
          </div>
        </li>`;
      })
      .join("");

    const slotsHtml = bar().slots
      .map((skillId, index) => {
        const locked = index >= unlockedSlots();
        const skill = skillId ? skillById(bar(), skillId) : undefined;
        const label = skill?.name ?? (locked ? "ล็อก" : "ว่าง");
        const icon = skill?.iconDataUrl ?? "";
        const on = pendingAssignSlot === index ? " skill-slot--pick" : "";
        const previewOn =
          skillId && previewSkillId === skillId ? " skill-slot--preview" : "";
        const lockClass = locked ? " skill-slot--locked" : "";
        const autoOn = bar().slotAuto[index] === true;
        const autoDisabled = locked || !skill;
        return `
        <div class="skill-hotbar-slot">
          <button type="button" class="skill-slot${on}${previewOn}${lockClass}" data-slot-index="${index}" ${skillId ? `data-slot-skill-id="${escapeHtml(skillId)}"` : ""} ${locked ? "disabled" : ""} title="${escapeHtml(label)}">
            ${icon ? `<img src="${escapeHtml(icon)}" alt="" width="36" height="36" />` : `<span class="skill-slot-num">${index + 1}</span>`}
          </button>
          <label class="skill-slot-auto${autoOn ? " skill-slot-auto--on" : ""}" title="Auto ในบ้านเมื่อตีและคูลดาวน์พร้อม">
            <input type="checkbox" class="skill-slot-auto-input" data-slot-auto="${index}" ${autoOn ? "checked" : ""} ${autoDisabled ? "disabled" : ""} />
            <span>Auto</span>
          </label>
        </div>`;
      })
      .join("");

    const navHtml = options.embedded
      ? ""
      : `
        <div class="skills-game-nav home-actions">
          <a class="btn btn-primary" href="/home">กลับบ้าน</a>
          <a class="btn btn-ghost" href="/home?character=1">ตัวละคร</a>
        </div>`;

    const manageTitle =
      (options.cardPool ?? "player") === "pet" ? "จัดการสกิลสัตว์เลี้ยง" : "จัดการสกิล";

    const headerHtml = petDetailLayout
      ? ""
      : options.embedded
      ? `<p class="hint home-skills-panel-meta">${poolCfg.label} · ช่อง ${unlockedSlots()}/${SKILL_SLOT_MAX} · เทมเพลต ${SKILL_TEMPLATE_FRAME_WIDTH}×${SKILL_TEMPLATE_FRAME_HEIGHT}px</p>`
      : `
        <header class="skills-game-header">
          <p class="eyebrow">${poolCfg.label}</p>
          <h1 id="skills-title">${manageTitle}</h1>
          <p class="lede">ช่อง ${unlockedSlots()}/${SKILL_SLOT_MAX} · ครอบ 6 เฟรม · เทมเพลต ${SKILL_TEMPLATE_FRAME_WIDTH}×${SKILL_TEMPLATE_FRAME_HEIGHT}px</p>
        </header>`;

    const mainAria = options.embedded
      ? `aria-label="${manageTitle}"`
      : `aria-labelledby="skills-title"`;

    const previewSkill = previewSkillId ? skillById(bar(), previewSkillId) : undefined;
    const usagePanelHtml = showUsageDemo()
      ? `
        <section class="game-panel skills-usage-panel" aria-labelledby="skill-usage-title">
          <h2 id="skill-usage-title" class="game-panel-title skill-panel-kicker">${previewSkill ? escapeHtml(previewSkill.name) : "ตัวอย่างการใช้งาน"}</h2>
          <div id="skill-usage-demo-host"></div>
        </section>`
      : "";

    const previewColumnHtml = options.embedded
      ? `
        <aside class="skills-preview-column" aria-label="${(options.cardPool ?? "player") === "pet" ? "สัตว์เลี้ยงและตัวอย่างสกิล" : "ตัวละครและตัวอย่างสกิล"}">
          ${
            previewSkillId
              ? usagePanelHtml
              : `
            <div id="skills-char-preview-host" class="skills-char-preview-host"></div>
            <p class="hint skills-preview-hint">กดสกิลในช่องหรือคลังเพื่อดูตัวอย่าง</p>`
          }
        </aside>`
      : "";

    const libraryEmpty =
      libraryItems ||
      `<li class="skill-inv-empty"><span>ยังไม่มีสกิล</span><span class="hint">กด + สร้างสกิลใหม่</span></li>`;

    const hotbarHint =
      pendingAssignSlot != null
        ? `เลือกสกิลจากคลังเพื่อใส่ช่อง ${pendingAssignSlot + 1}`
        : petDetailLayout
          ? "กดช่องว่างแล้วเลือกจากคลัง · กดช่องที่มีสกิลเพื่อดูตัวอย่าง"
          : (options.cardPool ?? "player") === "pet"
            ? "กดช่องว่างแล้วเลือกจากคลัง · กดช่องที่มีสกิลเพื่อดูตัวอย่าง · Auto ใช้เมื่อสัตว์เลี้ยงต่อสู้"
            : "กดช่องว่างแล้วเลือกจากคลัง · กดช่องที่มีสกิลเพื่อดูตัวอย่าง · ติ๊ก Auto ต่อช่อง";

    const hotbarSection = `
        <section class="game-panel skill-hotbar-panel${petDetailLayout ? " pet-detail-hotbar-panel" : ""}" aria-label="แถบสกิล">
          <h2 class="game-panel-title skill-panel-kicker">ช่องสกิลของคุณ${
            petDetailLayout
              ? ` <span class="pet-detail-slot-count">${unlockedSlots()}/${SKILL_SLOT_MAX}</span>`
              : ""
          }</h2>
          <div class="skill-slots-row skill-slots-row--game${petDetailLayout ? " pet-detail-skill-slots" : ""}">${slotsHtml}</div>
          <p class="hint game-panel-hint">${hotbarHint}</p>
        </section>`;

    const manageColumnHtml = `
        ${hotbarSection}

        <section class="game-panel skill-library-section skill-inventory-panel${petDetailLayout ? " pet-detail-library-panel" : ""}" aria-label="คลังสกิล">
          <div class="skill-inventory-head">
            <div>
              <h2 class="game-panel-title skill-panel-kicker">คลังสกิล</h2>
              <p class="hint skill-card-inventory">${
                petDetailLayout
                  ? `คลัง <strong>${bar().library.length}/${poolCfg.librarySlotsUnlocked(eco())}</strong> · บัตรสร้าง <strong>${poolCfg.createCards(eco())}</strong> · อัปเกรด <strong>${poolCfg.cooldownCards(eco())}</strong> คูลดาวน์ · <strong>${poolCfg.apCards(eco())}</strong> AP`
                  : `คลัง <strong>${bar().library.length}/${poolCfg.librarySlotsUnlocked(eco())}</strong> (สูงสุด ${SKILL_LIBRARY_MAX}) · บัตรสร้าง <strong>${poolCfg.createCards(eco())}</strong> · คูลดาวน์ <strong>${poolCfg.cooldownCards(eco())}</strong> · AP <strong>${poolCfg.apCards(eco())}</strong> · เวลาบัฟ <strong>${poolCfg.buffDurationCards(eco())}</strong> · ระยะหมู่ <strong>${poolCfg.aoeRangeCards(eco())}</strong> · ร้าน Cash`
              }</p>
            </div>
            <button type="button" class="skill-add-btn skill-add-btn--fab" id="skill-add" aria-label="สร้างสกิลใหม่" title="สร้างสกิลใหม่">
              <span aria-hidden="true">+</span>
              <span class="skill-add-btn-label">สกิลใหม่</span>
            </button>
          </div>
          <ul class="skill-inv-grid">${libraryEmpty}</ul>
          <p id="skill-library-error" class="form-error" role="alert" hidden></p>
        </section>`;

    root.innerHTML = `
      <main class="skills-game-frame" ${mainAria}>
        ${headerHtml}

        ${
          petDetailLayout
            ? `<div class="pet-detail-skills-stack">${manageColumnHtml}</div>`
            : options.embedded
              ? `
          <div class="skills-manage-layout">
            ${previewColumnHtml}
            <div class="skills-manage-column">${manageColumnHtml}</div>
          </div>`
              : `${usagePanelHtml}${manageColumnHtml}`
        }

        ${navHtml}
      </main>
    `;

    bindMain();
    syncEditorModal();
    if (petDetailLayout && previewSkillId) {
      mountUsageDemo();
    } else if (options.embedded && previewSkillId) {
      mountUsageDemo();
    } else {
      mountCharPreview();
      if (!options.embedded) mountUsageDemo();
    }
  };

  const syncEditorBodyClass = () => {
    if (!options.embedded && !options.editorInRoot) return;
    document.body.classList.toggle("home-skill-editor-open", editorOpen);
  };

  const syncEditorModal = () => {
    if (!useHomeSkillEditor && !root.querySelector("#skill-editor-modal-local")) {
      root.insertAdjacentHTML(
        "beforeend",
        `
        <div id="skill-editor-modal-local" class="home-skill-editor-modal" hidden aria-hidden="true">
          <button type="button" class="home-skill-editor-backdrop" data-close-skill-editor aria-label="ปิด"></button>
          <div class="home-skill-editor-dialog" role="dialog" aria-modal="true" aria-labelledby="skill-editor-title">
            <div id="skill-editor-host-local" class="home-skill-editor-host"></div>
          </div>
        </div>`,
      );
    }

    const modal = editorModal();
    const host = editorHost();
    if (!modal || !host) return;

    if (!editorOpen) {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      host.innerHTML = "";
      stopPreview?.();
      stopPreview = undefined;
      syncEditorBodyClass();
      return;
    }

    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    host.innerHTML = editorPanelHtml();
    bindEditor();
    mountPreviewIfNeeded();
    syncEditorBodyClass();
    requestAnimationFrame(() => {
      host.querySelector<HTMLInputElement>("#skill-name")?.focus({ preventScroll: true });
      mountPreviewIfNeeded();
    });
  };

  const showError = (message: string) => {
    const el = editorOpen
      ? editorHost()?.querySelector<HTMLElement>("#skill-form-error")
      : root.querySelector<HTMLElement>("#skill-library-error");
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  };

  const updateSubmitDisabled = () => {
    const submit = editorHost()?.querySelector<HTMLButtonElement>("#skill-create-submit");
    if (submit) submit.disabled = !draftSheet || !draftName.trim();
  };

  const bindEditor = () => {
    const host = editorHost();
    if (!host) return;

    host.querySelector("#skill-editor-close")?.addEventListener("click", () => {
      closeEditor();
    });

    host.querySelector("#skill-download-template")?.addEventListener("click", () => {
      downloadSkillMotionTemplate();
    });

    const removeWhiteInput = host.querySelector<HTMLInputElement>("#skill-remove-white-bg");
    if (removeWhiteInput) bindAutoRemoveWhiteBgCheckbox(removeWhiteInput);
    const removeBlackInput = host.querySelector<HTMLInputElement>("#skill-remove-black-bg");
    if (removeBlackInput) bindAutoRemoveBlackBgCheckbox(removeBlackInput);

    host.querySelector("#skill-apply-remove-bg")?.addEventListener("click", () => {
      if (!draftRawUrl) return;
      void (async () => {
        try {
          draftRawUrl = await applyBackgroundRemovalFromDataUrl(draftRawUrl!, {
            removeWhite: isAutoRemoveWhiteBgEnabled(),
            removeBlack: isAutoRemoveBlackBgEnabled(),
          });
          await applySkillParse();
          host.querySelector<HTMLElement>("#skill-form-error")?.setAttribute("hidden", "true");
          syncEditorModal();
        } catch (err) {
          showError(err instanceof Error ? err.message : "ลบพื้นหลังไม่สำเร็จ");
        }
      })();
    });

    const nameInput = host.querySelector<HTMLInputElement>("#skill-name");
    nameInput?.addEventListener("input", () => {
      draftName = nameInput.value;
      updateSubmitDisabled();
    });

    const kindSelect = host.querySelector<HTMLSelectElement>("#skill-kind");
    kindSelect?.addEventListener("change", () => {
      const nextKind = kindSelect.value === "buff" ? "buff" : "attack";
      if (nextKind === "buff" && draftKind !== "buff") {
        draftBuffAllocation = emptyAllocation();
      }
      draftKind = nextKind;
      syncEditorModal();
    });

    host.querySelectorAll<HTMLButtonElement>("[data-buff-stat-plus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const statId = btn.dataset.buffStatPlus as StatId | undefined;
        if (!statId || !STAT_IDS.includes(statId)) return;
        const next = allocateBuffStatPointForCards(
          draftEditorSkillApCards(),
          draftBuffAllocation,
          statId,
        );
        if (!next) return;
        draftBuffAllocation = next;
        syncEditorModal();
      });
    });

    host.querySelectorAll<HTMLButtonElement>("[data-buff-stat-minus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const statId = btn.dataset.buffStatMinus as StatId | undefined;
        if (!statId || !STAT_IDS.includes(statId)) return;
        const next = deallocateBuffStatPointForCards(draftBuffAllocation, statId);
        if (!next) return;
        draftBuffAllocation = next;
        syncEditorModal();
      });
    });

    const attackPatternSelect = host.querySelector<HTMLSelectElement>("#skill-attack-pattern");
    attackPatternSelect?.addEventListener("change", () => {
      draftAttackPattern = attackPatternSelect.value === "aoe" ? "aoe" : "single";
    });

    host.querySelector("#skill-sheet-file")?.addEventListener("change", () => {
      const input = host.querySelector<HTMLInputElement>("#skill-sheet-file");
      const file = input?.files?.[0];
      if (!file) return;
      void (async () => {
        try {
          const dataUrl = await readImageFileAsDataUrl(file);
          await processSkillImage(dataUrl, true);
          host.querySelector<HTMLElement>("#skill-form-error")?.setAttribute("hidden", "true");
          syncEditorModal();
        } catch (err) {
          showError(err instanceof Error ? err.message : "อัปโหลดไม่สำเร็จ");
        }
      })();
    });

    host.querySelector("#skill-create-submit")?.addEventListener("click", () => {
      const name = draftName.trim();
      if (!name || !draftSheet || !draftIcon) return;
      const kind = draftKind;
      const existing = draftEditingId ? skillById(bar(), draftEditingId) : undefined;
      const base: SkillDefinition = {
        id: draftEditingId ?? newSkillId(),
        name,
        kind,
        durationMs: kind === "buff" ? SKILL_DEFAULT_BUFF_DURATION_MS : 0,
        spritesheetDataUrl: draftSheet,
        grid: draftGrid,
        iconDataUrl: draftIcon,
        frameRate: 14,
        createdAt: existing?.createdAt ?? Date.now(),
        sourceSheetDataUrl: draftRawUrl ?? undefined,
        frameLayout: draftRawUrl ? draftFrameLayout : undefined,
        attackPattern: kind === "attack" ? draftAttackPattern : undefined,
        buffStatBonus: kind === "buff" ? buffStatBonusRecord(draftBuffAllocation) : undefined,
        skillApCards: kind === "buff" ? existing?.skillApCards : undefined,
        buffDurationCards: kind === "buff" ? existing?.buffDurationCards : undefined,
        aoeRangeCards:
          kind === "attack" && draftAttackPattern === "aoe"
            ? existing?.aoeRangeCards
            : undefined,
        cooldownCards: existing?.cooldownCards,
        damageMultiplier: kind === "attack" ? existing?.damageMultiplier : undefined,
      };

      if (draftEditingId) {
        const index = bar().library.findIndex((s) => s.id === draftEditingId);
        if (index >= 0) bar().library[index] = base;
      } else {
        const gate = poolCfg.canAddToLibrary(bar().library.length, eco());
        if (!gate.ok) {
          showError(gate.reason ?? "ไม่สามารถสร้างสกิลได้");
          return;
        }
        const nextEco = poolCfg.consumeCreateCard(eco());
        if (!nextEco) {
          showError("ไม่มีบัตรสร้างสกิล");
          return;
        }
        options.onEconomyUpdated?.(nextEco);
        persistEconomy();
        bar().library.unshift(base);
      }
      persist();
      closeEditor();
    });
  };

  const selectPreviewSkill = (skillId: string) => {
    if (!skillById(bar(), skillId)) return;
    previewSkillId = skillId;
    render();
  };

  const bindMain = () => {
    root.querySelector("#skill-add")?.addEventListener("click", () => {
      openCreateEditor();
    });

    root.querySelectorAll<HTMLElement>(".skill-inv-card[data-skill-id]").forEach((card) => {
      card.addEventListener("click", (event) => {
        if ((event.target as HTMLElement).closest(".skill-inv-actions")) return;
        const id = card.dataset.skillId;
        if (!id) return;
        selectPreviewSkill(id);
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-upgrade-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.upgradeSkill;
        if (!id) return;
        const index = bar().library.findIndex((s) => s.id === id);
        if (index < 0) return;
        const result = poolCfg.upgradeCooldown(eco(), bar().library[index]);
        if (!result.ok) {
          showError(result.reason);
          return;
        }
        options.onEconomyUpdated?.(result.economy);
        bar().library[index] = result.skill;
        root.querySelector<HTMLElement>("#skill-library-error")?.setAttribute("hidden", "true");
        persistEconomy();
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-upgrade-aoe-range-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.upgradeAoeRangeSkill;
        if (!id) return;
        const index = bar().library.findIndex((s) => s.id === id);
        if (index < 0) return;
        const result = poolCfg.upgradeAoe(eco(), bar().library[index]);
        if (!result.ok) {
          showError(result.reason);
          return;
        }
        options.onEconomyUpdated?.(result.economy);
        bar().library[index] = result.skill;
        root.querySelector<HTMLElement>("#skill-library-error")?.setAttribute("hidden", "true");
        persistEconomy();
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-upgrade-duration-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.upgradeDurationSkill;
        if (!id) return;
        const index = bar().library.findIndex((s) => s.id === id);
        if (index < 0) return;
        const result = poolCfg.upgradeDuration(eco(), bar().library[index]);
        if (!result.ok) {
          showError(result.reason);
          return;
        }
        options.onEconomyUpdated?.(result.economy);
        bar().library[index] = result.skill;
        root.querySelector<HTMLElement>("#skill-library-error")?.setAttribute("hidden", "true");
        persistEconomy();
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-upgrade-ap-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.upgradeApSkill;
        if (!id) return;
        const index = bar().library.findIndex((s) => s.id === id);
        if (index < 0) return;
        const result = poolCfg.upgradeAp(eco(), bar().library[index]);
        if (!result.ok) {
          showError(result.reason);
          return;
        }
        options.onEconomyUpdated?.(result.economy);
        bar().library[index] = result.skill;
        root.querySelector<HTMLElement>("#skill-library-error")?.setAttribute("hidden", "true");
        persistEconomy();
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-edit-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.editSkill;
        if (!id) return;
        openEditEditor(id);
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-slot-index]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const index = Number(btn.dataset.slotIndex);
        if (!Number.isFinite(index) || index >= unlockedSlots()) return;
        if (pendingAssignSlot != null) {
          pendingAssignSlot = index;
          render();
          return;
        }
        const equippedId = btn.dataset.slotSkillId;
        if (equippedId) {
          selectPreviewSkill(equippedId);
          return;
        }
        pendingAssignSlot = index;
        render();
      });
    });

    root.querySelectorAll<HTMLInputElement>("[data-slot-auto]").forEach((input) => {
      input.addEventListener("change", () => {
        const index = Number(input.dataset.slotAuto);
        if (!Number.isFinite(index) || index >= unlockedSlots()) return;
        if (!bar().slots[index]) {
          input.checked = false;
          return;
        }
        bar().slotAuto[index] = input.checked;
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-assign-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const skillId = btn.dataset.assignSkill;
        if (!skillId) return;
        if (pendingAssignSlot == null) {
          showError("กดช่องสกิลก่อน แล้วเลือกใส่");
          return;
        }
        assignSkillToSlot(bar(), pendingAssignSlot, skillId);
        pendingAssignSlot = null;
        const err = root.querySelector<HTMLElement>("#skill-library-error");
        err?.setAttribute("hidden", "true");
        persist();
        render();
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-remove-skill]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.removeSkill;
        if (!id) return;
        if (draftEditingId === id) closeEditor();
        bar().library = bar().library.filter((s) => s.id !== id);
        bar().slots = bar().slots.map((slotId) => (slotId === id ? null : slotId));
        persist();
        render();
      });
    });
  };

  const onEditorModalClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target.closest("[data-close-skill-editor]")) return;
    closeEditor();
  };
  const homeEditorModalEl = useHomeSkillEditor
    ? document.getElementById("home-skill-editor-modal")
    : null;
  homeEditorModalEl?.addEventListener("click", onEditorModalClick);
  if (!useHomeSkillEditor) {
    root.addEventListener("click", onEditorModalClick);
  }

  render();

  return {
    teardown: () => {
      document.body.classList.remove("home-skill-editor-open");
      homeEditorModalEl?.removeEventListener("click", onEditorModalClick);
      if (!useHomeSkillEditor) {
        root.removeEventListener("click", onEditorModalClick);
      }
      stopPreview?.();
      stopUsageDemo?.();
      stopCharPreview?.();
      const modal = editorModal();
      if (modal) {
        modal.hidden = true;
        editorHost()?.replaceChildren();
      }
      root.replaceChildren();
    },
    refresh: () => {
      previewSkillId = null;
      render();
    },
  };
}
